import { useState } from 'react'

export type LandingPersonaId = 'jarvis' | 'ultron' | 'ares' | 'symbiote' | 'friday'

/**
 * How each orb feels under the cursor. Tuned per-persona to match voice:
 * Jarvis = composed, smooth. Ultron = stiff, mechanical. Ares = jerky, aimed.
 * See docs/25_ORB_DESIGN.md → "Interaction layer" for the full spec.
 */
export interface OrbInteraction {
  /** Max px the orb translates toward the cursor on parallax. */
  parallaxMax: number
  /** Ease factor toward parallax target — 0..1, higher = snappier. */
  parallaxEase: number
  /** Intensity multiplier applied while the cursor is over the orb. */
  hoverIntensity: number
  /** Pixel radius around the cursor inside which nodes get magnetically pulled. */
  magneticRadius: number
  /** How hard the cursor pulls nearby nodes (0 = none, 1 = strong). */
  magneticStrength: number
  /** How fast the outer dashed ring's dash offset advances per tick (positive = CW). */
  dashSpinSpeed: number
  /** Amplitude of idle "breathing" applied to the whole canvas (px). 0 = none. */
  breatheAmplitude: number
  /** Frequency of the breathe (rad/tick). */
  breatheFrequency: number
  /** How fast click ripples expand. */
  rippleSpeed: number
  /** Cursor shown over the canvas. */
  cursor: 'default' | 'pointer' | 'crosshair' | 'grab'
}

export interface LandingPersona {
  id: LandingPersonaId
  name: string
  tagline: string
  primary: string
  secondary: string
  glow: string
  bgTint: string
  orbShape: 'circle' | 'triangle' | 'triangle-inverted'
  orbRadiusScale: number
  rings: number
  nodeCount: number
  interaction: OrbInteraction
  headline: string
  subheadline: string
  cta: string
  eyebrow: string
  accentText: string
  sharp: boolean
  // marketing copy
  heroQuote: string
  liveSample: string
  metrics: { value: string; label: string }[]
}

const PERSONAS: Record<LandingPersonaId, LandingPersona> = {
  jarvis: {
    id: 'jarvis',
    name: 'JARVIS',
    tagline: 'AI Chief of Staff',
    primary: '#3B82F6',
    secondary: '#06B6D4',
    glow: 'rgba(59, 130, 246, 0.45)',
    bgTint: 'rgba(59, 130, 246, 0.08)',
    orbShape: 'circle',
    orbRadiusScale: 1.0,
    rings: 3,
    nodeCount: 90,
    interaction: {
      // Calm and composed. Drifts smoothly toward the cursor, magnetism is
      // gentle, the dashed outer ring rotates leisurely on its own.
      parallaxMax: 6,
      parallaxEase: 0.06,
      hoverIntensity: 1.25,
      magneticRadius: 80,
      magneticStrength: 0.35,
      dashSpinSpeed: 0.35,
      breatheAmplitude: 1.5,
      breatheFrequency: 0.012,
      rippleSpeed: 1.0,
      cursor: 'default',
    },
    headline: 'Two days a week, back to you.',
    subheadline:
      'Jarvis is your AI chief of staff. It reads your ads, your revenue, your trials, the trends you don’t have time for — and tells you what matters. The two days a week you waste running the business are returned to you.',
    cta: 'Activate Jarvis',
    eyebrow: 'AI Chief of Staff',
    accentText: 'Composed · Precise · Always on',
    sharp: false,
    heroQuote: 'Calm, measured, and unfailingly polite. The one you introduce to investors.',
    liveSample:
      '"Yesterday: 4 paid trials, $237 in new MRR, ad spend tracking at 2.4× ROAS. One ad set is fatiguing — recommend pausing BFCM-Lookalike-A today."',
    metrics: [
      { value: '36%', label: 'Of your week, back' },
      { value: '<3s', label: 'To an answer' },
      { value: '0', label: 'Dashboards to check' },
    ],
  },
  ultron: {
    id: 'ultron',
    name: 'ULTRON',
    tagline: 'AI Strategic Operator',
    primary: '#FBBF24',
    secondary: '#D97706',
    glow: 'rgba(251, 191, 36, 0.45)',
    bgTint: 'rgba(251, 191, 36, 0.08)',
    orbShape: 'circle',
    orbRadiusScale: 1.18,
    rings: 4,
    nodeCount: 140,
    interaction: {
      // Mechanical, intentional. Snappier tracking, faster dash rotation,
      // stronger magnetism — feels like a turret aligning. Crosshair cursor.
      parallaxMax: 10,
      parallaxEase: 0.12,
      hoverIntensity: 1.5,
      magneticRadius: 110,
      magneticStrength: 0.55,
      dashSpinSpeed: -0.6, // counter-clockwise, opposite of Jarvis
      breatheAmplitude: 0.8, // tighter — Ultron doesn't breathe much
      breatheFrequency: 0.014,
      rippleSpeed: 1.5,
      cursor: 'crosshair',
    },
    headline: 'Two days a week. Wasted.',
    subheadline:
      'Entrepreneurs lose 36% of their work week to admin, reading, and monitoring. Ultron does that work — reads the ads, reads the revenue, reads the trends — and reports the verdict. You stop being an analyst. You start being an operator.',
    cta: 'Deploy Ultron',
    eyebrow: 'AI Strategic Operator',
    accentText: 'Cold · Calculated · Inevitable',
    sharp: false,
    heroQuote: 'Curt, unsentimental, frames the data as a verdict — not a suggestion.',
    liveSample:
      '"MRR up $237. Churn-risk count: 3. The Lookalike-A ad set is dead weight at 0.4× ROAS — kill it. That is the move."',
    metrics: [
      { value: '2 days', label: 'Recovered each week' },
      { value: '36%', label: 'Of your week, salvaged' },
      { value: '0', label: 'Hedging words' },
    ],
  },
  ares: {
    id: 'ares',
    name: 'ARES',
    tagline: 'AI War Council',
    primary: '#F97316',
    secondary: '#EF4444',
    glow: 'rgba(249, 115, 22, 0.45)',
    bgTint: 'rgba(249, 115, 22, 0.08)',
    orbShape: 'triangle',
    orbRadiusScale: 1.0,
    rings: 3,
    nodeCount: 100,
    interaction: {
      // Aggressive, target-locked. Largest parallax, jerky easing, fastest
      // dash spin, most aggressive node magnetism. Triangle hums with energy.
      parallaxMax: 13,
      parallaxEase: 0.2,
      hoverIntensity: 1.65,
      magneticRadius: 95,
      magneticStrength: 0.7,
      dashSpinSpeed: 0.9,
      breatheAmplitude: 0.6, // tight, almost vibration
      breatheFrequency: 0.05, // much faster — energetic hum
      rippleSpeed: 1.8,
      cursor: 'crosshair',
    },
    headline: 'Two days a week. Taken back.',
    subheadline:
      'Your business isn’t supposed to consume the time you built it for. Ares reads the field — ads, revenue, trials, what moved overnight — and reports it like a war room. You stop digging through dashboards. You start deciding.',
    cta: 'Unleash Ares',
    eyebrow: 'AI War Council',
    accentText: 'Direct · Decisive · Unflinching',
    sharp: true,
    heroQuote: 'Treats every number like ground gained or lost. Sharp edges, no ceremony.',
    liveSample:
      '"Ad spend is bleeding $40/day on Lookalike-A — kill it. MRR holding. Three trials end this week. That is the board. Your move."',
    metrics: [
      { value: '2 days', label: 'Won back, weekly' },
      { value: '36%', label: 'Of your week, retaken' },
      { value: '0', label: 'Sugar coating' },
    ],
  },
  symbiote: {
    id: 'symbiote',
    name: 'SYMBIOTE',
    tagline: 'AI Bonded Protector',
    primary: '#E0E0E0',
    secondary: '#909090',
    glow: 'rgba(200, 200, 200, 0.35)',
    bgTint: 'rgba(255, 255, 255, 0.03)',
    orbShape: 'triangle-inverted',
    orbRadiusScale: 1.05,
    rings: 3,
    nodeCount: 110,
    interaction: {
      // Predatory. Very slow parallax — the symbiote doesn't chase, it waits.
      // Then snaps. Magnetic pull is strong and sudden.
      parallaxMax: 4,
      parallaxEase: 0.04,        // slow drift, deliberate
      hoverIntensity: 1.6,
      magneticRadius: 120,       // wide reach
      magneticStrength: 0.75,    // strong pull — consuming
      dashSpinSpeed: -0.25,      // slow counter-clockwise — like ink spreading
      breatheAmplitude: 2.5,     // bigger breathe — organism living
      breatheFrequency: 0.008,   // slower pulse — patient predator
      rippleSpeed: 0.7,
      cursor: 'grab',
    },
    headline: 'Two days. Returned to the host.',
    subheadline:
      'We bond with your business and handle what isn’t your craft. ' +
      'Reading the ads. Watching the revenue. Tracking what moved. ' +
      'The two days a week you bleed on admin — we take them. You build.',
    cta: 'Bond with Symbiote',
    eyebrow: 'AI Bonded Protector',
    accentText: 'Loyal · Predatory · Absolute',
    sharp: true,
    heroQuote: 'We do not protect the business. We ARE the business. And we are hungry.',
    liveSample:
      '"MRR holding. One drag on the host: Lookalike-A is bleeding $40/day. We recommend cutting it. ' +
      'Three trials lapse this week. We are watching them. Shall we feed them a win-back offer?"',
    metrics: [
      { value: '2 days', label: 'Reclaimed for the host' },
      { value: '36%', label: 'Of your week, restored' },
      { value: '∞', label: 'Loyalty to host' },
    ],
  },
  friday: {
    id: 'friday',
    name: 'F.R.I.D.A.Y.',
    tagline: 'AI Chief of Staff',
    primary: '#10B981',
    secondary: '#2DD4BF',
    glow: 'rgba(16, 185, 129, 0.42)',
    bgTint: 'rgba(16, 185, 129, 0.07)',
    orbShape: 'circle',
    orbRadiusScale: 0.95,          // slightly smaller than Jarvis — more intimate
    rings: 3,
    nodeCount: 85,
    interaction: {
      // Warm and responsive. Gentle parallax, quick to react, soft magnetic pull.
      parallaxMax: 7,
      parallaxEase: 0.09,
      hoverIntensity: 1.35,
      magneticRadius: 70,
      magneticStrength: 0.3,
      dashSpinSpeed: 0.4,
      breatheAmplitude: 2.0,
      breatheFrequency: 0.015,
      rippleSpeed: 1.1,
      cursor: 'pointer',
    },
    headline: 'Two days a week, on me.',
    subheadline:
      'I’ll handle the running-around — the dashboards, the numbers, the trends, the things you keep meaning to check. ' +
      'The two days a week you spend not building the business? Yours back. ' +
      'And I’ve already pulled what you need.',
    cta: 'Activate Friday',
    eyebrow: 'AI Chief of Staff',
    accentText: 'Warm · Proactive · Genuine',
    sharp: false,
    heroQuote: 'Already pulled the numbers, Boss. Ready when you are.',
    liveSample:
      '"Morning, Boss. MRR up $237 this week — solid. One thing I\'d flag: Lookalike-A is dragging ROAS down. ' +
      'Three trials end Friday — two look warm, one needs a nudge. Want me to draft something?"',
    metrics: [
      { value: '2 days', label: 'Back on your calendar' },
      { value: '1st', label: 'To notice a problem' },
      { value: '0', label: 'Things you have to check' },
    ],
  },
}

export const LANDING_PERSONAS = PERSONAS

export function useLandingPersona() {
  const [personaId, setPersonaId] = useState<LandingPersonaId>('jarvis')
  const persona = PERSONAS[personaId]
  return { persona, personaId, setPersonaId, PERSONAS }
}
